import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { inject, injectable } from 'inversify';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import { SliceType } from '@/engine/app/modules/images/interfaces';
import { TYPES } from '@/engine/ioc';
import type { ViewController } from '@/engine/app/View.controller';
import * as THREE from 'three';
import type { CameraMemento, ICameraController, IViewPortManager } from '@/engine/app/interfaces';
import { ViewPortKind } from '@/engine/app/interfaces';
import type { MinMax } from '@/engine/app/modules/images/ImageStackController';
import { AutoEventSubscription } from '@/engine/Utilities/AutoEventSubscription';
import { noRecursionScope } from '@/engine/Utilities/FunctorsUtils';

class _Storage {
  private readonly _cache = new Map<string, string>();

  private _invalidateCache(key: string, value: string | null): boolean {
    if (value === null) {
      if (this._cache.has(key)) {
        this._cache.delete(key);
        return true;
      } else {
        return false;
      }
    } else {
      const val = this._cache.get(key);
      if (val !== value) {
        this._cache.set(key, value);
        return true;
      } else {
        return false;
      }
    }
  }

  public setValue(key: string, value: unknown): boolean {
    if (value !== undefined) {
      const val = JSON.stringify(value);
      if (this._invalidateCache(key, val)) {
        localStorage.setItem(key, val);
        return true;
      } else {
        return false;
      }
    } else {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        return true;
      } else {
        return false;
      }
    }
  }

  public getValue<T>(key: string, funcUpdate: (val: T) => void, funcErase?: () => void, forceUpdate = false): boolean {
    const val = localStorage.getItem(key);
    if (this._invalidateCache(key, val) || forceUpdate) {
      if (val !== null) {
        funcUpdate(JSON.parse(val) as T);
      } else if (funcErase) {
        funcErase();
      }
      return true;
    } else {
      return false;
    }
  }
}

@injectable()
export class SynchronizeTabsController {
  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private _disposables = new DisposablesBasket();

  private _storage = new _Storage();

  public constructor(
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService
  ) {
    this._viewController = viewController;
    this._imagesService = imagesService;
  }

  public activate(): void {
    this._disposables.dispose();

    const scope = noRecursionScope();

    // PUBLISH

    this._disposables.add(
      this._imagesService.subscribeToSliceVisualizationChange(SliceType.AXIAL, () =>
        scope((): void => {
          this._storage.setValue('images.contrast', this._imagesService.getContrast());
        })
      )
    );

    this._disposables.add(
      this._imagesService.subscribeToSynchronizationPointChanged((pt: THREE.Vector3) =>
        scope((): void => {
          this._storage.setValue('images.syncPoint', pt.toArray());
          const m = this._imagesService.getSlicesOrientationsMementos();
          this._storage.setValue(
            'images.orientations',
            Array.from(m.entries()).map((val) => [val[0], val[1].toArray()])
          );
        })
      )
    );

    const cameraFunc = (vpm: IViewPortManager, cam: ICameraController): void =>
      scope(() => {
        this._storage.setValue('views.cameras.' + vpm.getDescriptor(), cam.getMemento());
      });
    for (const vpm of this._viewController
      .getRegisteredViewPorts(ViewPortKind.ImageSlice)
      .concat(this._viewController.getRegisteredViewPorts(ViewPortKind.ThreeD))) {
      const cam = vpm.getCameraController();
      this._disposables.add(cam.onCameraModified(() => cameraFunc(vpm, cam)));
      cameraFunc(vpm, cam);
    }

    // SUBSCRIBE

    const syncFunc = (): void =>
      scope(() => {
        this._storage.getValue<MinMax>('images.contrast', (val) => {
          this._imagesService.setContrast(val);
        });

        const wasPointSet = this._storage.getValue<Array<number>>('images.syncPoint', (val) => {
          this._imagesService.syncImages(new THREE.Vector3().fromArray(val));
        });

        const wasOrientationSet = this._storage.getValue<Array<[SliceType, Array<number>]>>(
          'images.orientations',
          (val) => {
            const m = new Map(val.map((v) => [v[0], new THREE.Matrix4().fromArray(v[1])]));
            const pt = this._imagesService.getSlicesIntersectionPoint();
            for (const sliceType of this._imagesService.getRegisteredSliceTypes()) {
              this._imagesService.applyMpr(m, sliceType, pt);
            }
          }
        );

        const vmps = this._viewController
          .getRegisteredViewPorts(ViewPortKind.ImageSlice)
          .concat(this._viewController.getRegisteredViewPorts(ViewPortKind.ThreeD));
        for (const vpm of vmps) {
          this._storage.getValue<CameraMemento>(
            'views.cameras.' + vpm.getDescriptor(),
            (val) => {
              vpm.getCameraController().setMemento(val);
            },
            undefined,
            wasPointSet || wasOrientationSet
          );
        }
      });

    this._disposables.add(
      new AutoEventSubscription(
        window,
        'storage',
        (event): void => {
          if ((event as StorageEvent).storageArea === localStorage) {
            syncFunc();
          }
        },
        false
      )
    );

    syncFunc();
  }

  public deactivate(): void {
    this._disposables.dispose();
  }
}
